import React, { useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useIntersectionObserver } from "@/shared/hooks/useIntersectionObserver";
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MultiPetSelectProps } from "./types";
import { Button } from "@/shared/components/ui/Button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNavDots } from "@/shared/components/ui/carousel";
import PetCard, { PlaceholderPetCard } from "./PetCard";
import PetModal, { resolvePetModalLabel } from "../PetModal";
import { useMultiPetDiscountCopy } from "@/shared/contexts/MultiPetDiscount";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { StickyMenu } from "./StickyMenu";
import { cn } from "@/shared/utils";
import { Policy } from "@/shared/types/Quote.interface";
import { useCoverageEditorContext } from "../CoverageEditor";
import { BreakpointUtils } from "@/shared/utils/BreakpointUtils";
import { ADD_PET_MODAL_TRIGGERS } from "@/shared/utils/constants";
import { Badge } from "../ui/Badge";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

const MultiPetSelectCard: React.FC<Omit<MultiPetSelectProps, "variant">> = ({
    activePet,
    policyPets,
    handleActivePetChange,
    isQuoteUpdating,
    policies,
    petModalConfig,
    isMultiPetLinked,
    isApplyAllHidden,
    multipetToggle,
    userEditTrigger,
    showStickyPriceLabel,
    showStepLabel = false
}) => {
    const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

    const { appState, updateAppState } = useAppLayerContext();
    const { addPetModal, partnerOffer } = appState;
    const editorConfig = useCoverageEditorContext();
    const { getModalInitialValues, showButtonAnimations } = editorConfig;
    const { breakpoint } = useAppLayerContext().appState;
    const quoteId = useAppLayerContext().appState.quoteQuery?.data?.id;
    const multiPetCopy = useMultiPetDiscountCopy();
    const defaultCoverageSettings = policies[0]?.coverageSettings;

    const { ref: visibilityRef, isIntersecting } = useIntersectionObserver({
        initialIsIntersecting: true
    });
    const isAddAPetInView = isIntersecting ?? true;

    const isMultiPet = policies.length > 1;
    const dynamicRootMargin = isMultiPet ? "-7% 0px 0px 0px" : "-4% 0px 0px 0px";
    const { ref: priceObserverRef, isIntersecting: priceInView } = useIntersectionObserver({
        initialIsIntersecting: true,
        rootMargin: dynamicRootMargin
    });

    const isPriceVisible = priceInView ?? true;
    const stickyMenuVisible = showStickyPriceLabel ? !isPriceVisible : !isAddAPetInView;

    useEffect(() => {
        updateAppState({ isStickyMenuVisible: stickyMenuVisible });
    }, [stickyMenuVisible, updateAppState]);

    const handleAddPet = () => {
        updateAppState({ addPetModal: ADD_PET_MODAL_TRIGGERS.PET_CARD_CTA });
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        handleAddPet();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleAddPet();
        }
    };

    const handleCloseModal = async (editedPetId?: string) => {
        updateAppState({ addPetModal: undefined });

        // Focus the edited pet in the carousel
        if (editedPetId && carouselApi && !isMultiPetLinked) {
            const petIndex = policies.findIndex(pet => pet.id === editedPetId);
            if (petIndex !== -1) {
                carouselApi.scrollTo(petIndex);
                handleActivePetChange(editedPetId);
            }
        }
    };

    const handlePetSelection = React.useCallback(
        (selectedIndex: number) => {
            const petIdToSet = policyPets?.[selectedIndex]?.value;
            if (petIdToSet) {
                handleActivePetChange(petIdToSet);
            }
        },
        [policyPets, handleActivePetChange]
    );

    const handlePetSelectionRef = React.useRef(handlePetSelection);
    useEffect(() => {
        handlePetSelectionRef.current = handlePetSelection;
    }, [handlePetSelection]);

    const stableSetApi = React.useCallback((api: CarouselApi) => {
        if (api) setCarouselApi(api);
    }, []);

    useEffect(() => {
        if (!carouselApi) return;
        const handler = debounce((api: typeof carouselApi) => {
            const idx = api.selectedScrollSnap() ?? 0;
            handlePetSelectionRef.current(idx);
        }, 300);

        const onSelect = () => handler(carouselApi);
        carouselApi.on("select", onSelect);
        return () => {
            handler.cancel();
            carouselApi.off("select", onSelect);
        };
    }, [carouselApi]);

    const renderItems = () => {
        if (isApplyAllHidden || !isMultiPetLinked || (isMultiPetLinked && policies.length === 1)) {
            return policies.map((pet, index) => (
                <CarouselItem key={pet.id} className="h-full pl-0" data-selected={activePet.id === pet.id} data-testid={`pet-card-slide-${index}`}>
                    <PetCard
                        pet={pet}
                        isQuoteUpdating={isQuoteUpdating}
                        petModalConfig={petModalConfig}
                        activePetId={activePet.id ?? ""}
                        quoteId={quoteId ?? ""}
                        policiesCount={policies.length}
                        handleActivePetChange={handleActivePetChange}
                        isMultiPetLinked={isMultiPetLinked}
                        isApplyAllHidden={isApplyAllHidden}
                        onSelect={() => carouselApi?.scrollTo(index)}
                        priceRef={showStickyPriceLabel ? visibilityRef : undefined}
                    />
                </CarouselItem>
            ));
        }
        return (
            <CarouselItem className="h-full pl-0">
                <PetCard
                    pet={activePet}
                    isQuoteUpdating={isQuoteUpdating}
                    petModalConfig={petModalConfig}
                    activePetId={activePet.id ?? ""}
                    quoteId={quoteId ?? ""}
                    policiesCount={policies.length}
                    handleActivePetChange={handleActivePetChange}
                    isMultiPetLinked={isMultiPetLinked}
                    isApplyAllHidden={isApplyAllHidden}
                    priceRef={showStickyPriceLabel ? visibilityRef : undefined}
                />
            </CarouselItem>
        );
    };

    const activePetIndex = policies.findIndex(pet => !!pet.id && pet.id === activePet.id);

    const carouselOptions = React.useMemo(() => ({ startIndex: !isMultiPetLinked ? activePetIndex : 0 }), [isMultiPetLinked, activePetIndex]);

    const containerRef = useRef(null);

    const MAX_VISIBLE_CARDS = 6;

    const renderStackedCards = () => {
        const totalCards = policies.length;
        const leftCount = Math.min(Math.floor(MAX_VISIBLE_CARDS / 2), activePetIndex);
        const rightCount = Math.min(Math.floor(MAX_VISIBLE_CARDS / 2), totalCards - activePetIndex - 1);

        const leftCards = policies.slice(Math.max(0, activePetIndex - leftCount), activePetIndex).reverse();
        const rightCards = policies.slice(activePetIndex + 1, activePetIndex + 1 + rightCount);
        const policyToShow = policies[activePetIndex];

        return (
            <>
                {leftCards.map((pet, index) => renderCard(pet, -(leftCards.length - index)))}
                {!!policyToShow && renderCard(policyToShow, 0)}
                {rightCards.map((pet, index) => renderCard(pet, index + 1))}
            </>
        );
    };

    const renderCard = (pet: Policy, offset: number) => {
        const absOffset = Math.abs(offset);
        const bgColor = absOffset % 2 === 1 ? "bg-background-tertiary" : "bg-background-secondary";

        return (
            <m.div
                key={pet.id}
                style={{
                    position: "absolute",
                    width: "328px",
                    height: "100%",
                    zIndex: MAX_VISIBLE_CARDS - absOffset
                }}
                initial={{
                    x: offset * 328,
                    y: 0,
                    scale: 1,
                    opacity: 1
                }}
                animate={{
                    x: offset * 1,
                    y: absOffset * 7,
                    scale: 1 - absOffset * 0.04,
                    opacity: 1
                }}
                transition={{
                    duration: 0.3,
                    delay: absOffset * 0.1,
                    type: "spring",
                    stiffness: 120,
                    damping: 20
                }}
            >
                {offset === 0 ? (
                    <PetCard
                        pet={{ ...pet, name: `${policies.length} Pets` }}
                        isQuoteUpdating={isQuoteUpdating}
                        petModalConfig={petModalConfig}
                        activePetId={pet.id ?? ""}
                        quoteId={quoteId ?? ""}
                        policiesCount={policies.length}
                        handleActivePetChange={handleActivePetChange}
                        isMultiPetLinked={isMultiPetLinked}
                        priceRef={showStickyPriceLabel ? visibilityRef : undefined}
                    />
                ) : (
                    <PlaceholderPetCard pet={pet} bgColor={bgColor} />
                )}
            </m.div>
        );
    };

    if (policies.length === 0) return null;

    const showCarouselDots = (policies.length < 9 && breakpoint === "base") || (policies.length < 16 && breakpoint !== "base");

    const isMobileBreakpoint = breakpoint && BreakpointUtils.isMobile(breakpoint);
    const modalTriggerWrapperClass = {
        "w-full gap-4 flex-wrap": showStepLabel && isMobileBreakpoint && policies.length > 1
    };

    const buttonSize = isMobileBreakpoint ? "xs" : "sm";

    return (
        <>
            <StickyMenu isVisible={stickyMenuVisible} showStickyPriceLabel={showStickyPriceLabel} />
            <LazyMotion features={domAnimation}>
                <div className="flex h-full flex-col sm:items-center">
                    <div ref={containerRef} className={cn("relative flex w-full justify-center contain-layout", partnerOffer && "mt-9")}>
                        {partnerOffer && (
                            <m.div
                                className="absolute top-0 -z-10 -translate-x-1/2"
                                initial={{ y: 0 }}
                                animate={{ y: -36 }}
                                transition={{ delay: 1, duration: 0.5, type: "spring", stiffness: 140, damping: 18 }}
                            >
                                <Badge variant="success" className="flex justify-center gap-1 whitespace-nowrap rounded-lg rounded-b-none p-3">
                                    <FontAwesomeIcon icon={faTag} className="size-4" />
                                    <p className="text-xs font-medium leading-4">
                                        <strong>{partnerOffer.headline}</strong> {partnerOffer.offerText}
                                    </p>
                                </Badge>
                            </m.div>
                        )}
                        <AnimatePresence mode="popLayout" initial={false}>
                            {isMultiPetLinked && !isApplyAllHidden ? (
                                <m.div key="linked" className="relative h-[177px] w-[328px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <div className="size-full">{renderStackedCards()}</div>
                                </m.div>
                            ) : (
                                <m.div key="unlinked" initial={false} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <Carousel
                                        aria-label="Current pet"
                                        options={carouselOptions}
                                        className="grid auto-cols-[328px] grid-flow-col"
                                        setApi={stableSetApi}
                                        disabled={policies.length === 1 || isMultiPetLinked}
                                    >
                                        <div className="flex flex-col items-center">
                                            <CarouselContent className="flex h-full w-[328px] gap-1" contentWrapperClass="overflow-visible">
                                                {renderItems()}
                                            </CarouselContent>
                                            {showCarouselDots && <CarouselNavDots className="mt-4" testId="multipet" />}
                                        </div>
                                    </Carousel>
                                </m.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div ref={priceObserverRef} data-testid="sticky-price-observer" className="h-1 w-full" />
                    {(!petModalConfig.hideTrigger || userEditTrigger) && (
                        <div
                            data-testid="pet-card-actions"
                            className={cn(
                                "relative mt-4 flex justify-center",
                                userEditTrigger && "sm:grid sm:w-full sm:grid-cols-[minmax(max-content,1fr)_auto_1fr] sm:items-center sm:gap-3",
                                petModalConfig.hideTrigger && "hidden sm:grid",
                                modalTriggerWrapperClass
                            )}
                        >
                            {userEditTrigger && <div className="hidden sm:col-start-1 sm:flex sm:justify-start">{userEditTrigger}</div>}
                            {showStepLabel && isMobileBreakpoint && multipetToggle}
                            {!petModalConfig.hideTrigger && (
                                <PetModal
                                    initialValues={{ policies: [getModalInitialValues(defaultCoverageSettings, !!isMultiPetLinked)] }}
                                    triggerButton={
                                        <Button
                                            ref={showStickyPriceLabel ? undefined : visibilityRef}
                                            buttonAnimation={showButtonAnimations ? "heartbeat" : undefined}
                                            loading={isQuoteUpdating}
                                            loadingSpinnerPosition="start"
                                            onClick={handleAddPet}
                                            onTouchStart={handleTouchStart}
                                            onKeyDown={handleKeyDown}
                                            variant="outline-secondary"
                                            size={buttonSize}
                                            className={cn("bg-background-primary text-xs font-medium leading-4 sm:text-sm")}
                                            startDecorator={<FontAwesomeIcon icon={faPlus} className="size-4" />}
                                        >
                                            {resolvePetModalLabel(petModalConfig.triggerLabel, multiPetCopy) ?? multiPetCopy.addPetCta}
                                        </Button>
                                    }
                                    isOpen={addPetModal === ADD_PET_MODAL_TRIGGERS.PET_CARD_CTA}
                                    handleOpen={handleAddPet}
                                    onClose={handleCloseModal}
                                    config={petModalConfig}
                                    handleActivePetChange={handleActivePetChange}
                                />
                            )}
                        </div>
                    )}
                </div>
            </LazyMotion>
        </>
    );
};

export default MultiPetSelectCard;
